import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { IProcountorInvoicePreviewRow } from '../ZentuvoInvoicingApi';
import { EDITABLE_STATUS_OPTIONS, formatDateTime } from '../procountorInvoiceHelpers';

interface IInvoiceRowProps {
    row: IProcountorInvoicePreviewRow;
    isSelected: boolean;
    onToggleSelect: (orderRowId: number | string) => void;
    onStatusChange: (orderRowId: number | string, invoiceStatusValue: number) => void;
}

const InvoiceRow: React.FC<IInvoiceRowProps> = ({ row, isSelected, onToggleSelect, onStatusChange }) => (
    <TableRow
        sx={{
            backgroundColor: row.HasValidProcountorId ? undefined : 'rgba(255, 244, 229, 0.9)'
        }}
    >
        <TableCell padding="checkbox">
            <Checkbox
                checked={isSelected}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggleSelect(row.OrderRowId);
                }}
            />
        </TableCell>
        <TableCell>
            <Select
                size="small"
                sx={{
                    width: 'auto',
                    minWidth: 50,
                    display: 'inline-flex',
                    '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
                        width: 'auto !important',
                        minWidth: '50 !important'
                    },
                    '& .MuiSelect-select': {
                        width: 'auto !important',
                        minWidth: '50 !important'
                    },
                    '& .MuiOutlinedInput-input': {
                        width: 'auto !important',
                        minWidth: '50 !important'
                    }
                }}
                value={String(row.InvoiceStatusValue)}
                onChange={(event) => onStatusChange(row.OrderRowId, Number(event.target.value))}
            >
                {EDITABLE_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </TableCell>
        <TableCell>
            <Typography variant="body2">{row.InvoiceNumber}</Typography>
            <Typography variant="caption" color="text.secondary">
                <RouterLink to={`/orders/${row.OrderId}/basic`} target="_blank" rel="noopener noreferrer">
                    {row.OrderCollectionNumber}:{row.OrderNumber}
                </RouterLink>
            </Typography>
        </TableCell>
        <TableCell>{formatDateTime(row.CreatedAt)}</TableCell>
        <TableCell sx={{ minWidth: 260 }}>
            <Typography variant="body2">{row.ProductName || '-'}</Typography>
            <Typography variant="caption" color="text.secondary">
                {row.InvoiceComment || '-'}
            </Typography>
        </TableCell>
        <TableCell align="right">{row.Quantity}</TableCell>
        <TableCell align="right">{row.Price.toFixed(2)}</TableCell>
        <TableCell align="right">{row.TotalAmount.toFixed(2)}</TableCell>
        <TableCell sx={{ minWidth: 180 }}>
            <Typography variant="body2" noWrap sx={{ userSelect: 'text' }}>
                {row.AddressLine1 || row.AddressLine || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ userSelect: 'text' }}>
                {[row.AddressPostalNumber, row.AddressCity].filter((value) => !!value).join(' ') || '-'}
            </Typography>
        </TableCell>
        <TableCell>
            {row.HasValidProcountorId ? (
                <Chip
                    size="small"
                    color="success"
                    label="OK"
                    sx={{
                        '.MuiChip-label': {
                            userSelect: 'text'
                        }
                    }}
                />
            ) : (
                <Typography variant="caption" color="warning.main">
                    {row.ValidationMessage}
                </Typography>
            )}
        </TableCell>
        <TableCell sx={{ minWidth: 420 }}>
            <Box
                component="pre"
                sx={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: 12
                }}
            >
                {row.RowCsvLine}
            </Box>
        </TableCell>
    </TableRow>
);

export default InvoiceRow;
